import React, { useState, useEffect, useRef } from "react";
import { Progress, Modal, Checkbox } from "semantic-ui-react";
import { db, firebase } from "../../firebase";
import Autocomplete from "react-google-autocomplete";
import { Helmet } from "react-helmet-async";

export const BandwidthCalcPage = (props) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section id="bandwidth-calc-page">
      <Helmet>
        <title>{"Alluvion Bandwidth Calculator"}</title>
      </Helmet>
      <div className="bandwidth-calc-intro content-intro">
        <BandwidthCalc />
      </div>
    </section>
  );
};

export const BandwidthCalc = (props) => {
  const [video, setVideo] = useState(10);
  const [videoType, setVideoType] = useState("HD");
  const [email, setEmail] = useState(10);
  const [social, setSocial] = useState(20);
  const [videoCalls, setVideoCalls] = useState(5);
  const [cams, setCams] = useState(5);
  const [camsType, setCamsType] = useState("HD");
  const [voip, setVoip] = useState(20);
  const [media, setMedia] = useState(20);
  const [files, setFiles] = useState(20);
  const [totalBand, setTotalBand] = useState(0);

  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [business, setBusiness] = useState("");
  const [contactLocation, setContactLocation] = useState("");
  const [agreement, setAgreement] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailLeadGen, setEmailLeadGen] = useState("");
  const locationRef = useRef();
  const error = useRef();

  useEffect(() => {
    db.getSettings().then((res) => {
      setEmailLeadGen(res?.email.emailLeadGen ? res.email.emailLeadGen : "");
    });
  }, []);

  useEffect(() => {
    let total = 0;
    total +=
      (videoType === "HD" ? video * 10 : video * 25) +
      email * 1 +
      social * 5 +
      videoCalls * 4 +
      (camsType === "HD" ? cams * 10 : cams * 25) +
      voip * 2 +
      media * 2 +
      files * 10;
    setTotalBand(total);
  }, [
    video,
    videoType,
    email,
    social,
    videoCalls,
    cams,
    camsType,
    voip,
    media,
    files,
  ]);

  const getPlanType = () => {
    return totalBand > 100 && totalBand < 500
      ? "500 Mbps"
      : totalBand > 500
      ? "1 Gbps"
      : "100 Mbps";
  };

  const isInvalid =
    name === "" || contactEmail === "" || contactLocation === "";

  const onSubmit = (event) => {
    event.preventDefault();
    setEmailSending(true);

    firebase
      .sendBandwidthCalc({
        to: emailLeadGen,
        name: name,
        contactEmail: contactEmail,
        phone: phone ? phone : "N/A",
        business: business ? business : "N/A",
        location: contactLocation.formatted_address,
        agreement: agreement ? "Yes" : "No",
        message: contactMessage,
        video: video,
        videoType: videoType,
        email: email,
        social: social,
        videoCalls: videoCalls,
        cams: cams,
        camsType: camsType,
        voip: voip,
        media: media,
        files: files,
        totalBand: totalBand,
        planType: getPlanType(),
      })
      .then(() => {
        setName("");
        setContactEmail("");
        setPhone("");
        setBusiness("");
        setContactLocation("");
        setAgreement(false);
        setContactMessage("");
        setEmailSending(false);
        setEmailSent(true);
        const autoComp = document.getElementById("location-autocomplete");
        if (autoComp) {
          autoComp.value = "";
        }
      });
  };

  return (
    <section id="band-calc">
      <h2>Bandwidth Calculator</h2>

      <div className="band-items">
        <div className="band-item">
          <h5>Streaming Video</h5>
          <img
            src={require(`../../images/icon-play.png`)}
            alt="Streaming Video"
          />
          <div className="video-types">
            <div
              className={`video-type ${videoType === "HD" ? "active" : ""}`}
              onClick={() => setVideoType("HD")}
            >
              <p>HD</p>
            </div>
            <div
              className={`video-type ${videoType === "4K" ? "active" : ""}`}
              onClick={() => setVideoType("4K")}
            >
              <p>4K</p>
            </div>
          </div>
          <div className="band-count">
            <div className="band-count-input">
              <i className="icon icon-users" />
              <input
                type="number"
                value={video}
                onChange={(event) => setVideo(event.target.value)}
                min="0"
              />
            </div>
            <input
              type="range"
              value={video}
              onChange={(event) => setVideo(event.target.value)}
              min="0"
              max="25"
              step="1"
            />
          </div>
        </div>

        <div className="band-item">
          <h5>Web / Email</h5>
          <img src={require(`../../images/icon-email.png`)} alt="Web / Email" />
          <div className="band-count">
            <div className="band-count-input">
              <i className="icon icon-users" />
              <input
                type="number"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                min="0"
              />
            </div>
            <input
              type="range"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              min="0"
              max="25"
              step="1"
            />
          </div>
        </div>

        <div className="band-item">
          <h5>Social / Web Browsing</h5>
          <img
            src={require(`../../images/icon-website.png`)}
            alt="Social / Web Browsing"
          />
          <div className="band-count">
            <div className="band-count-input">
              <i className="icon icon-users" />
              <input
                type="number"
                value={social}
                onChange={(event) => setSocial(event.target.value)}
                min="0"
              />
            </div>
            <input
              type="range"
              value={social}
              onChange={(event) => setSocial(event.target.value)}
              min="0"
              max="25"
              step="1"
            />
          </div>
        </div>

        <div className="band-item">
          <h5>Group Video Calls</h5>
          <img
            src={require(`../../images/icon-webcam.png`)}
            alt="Group Video Calls"
          />
          <div className="band-count">
            <div className="band-count-input">
              <i className="icon icon-users" />
              <input
                type="number"
                value={videoCalls}
                onChange={(event) => setVideoCalls(event.target.value)}
                min="0"
              />
            </div>
            <input
              type="range"
              value={videoCalls}
              onChange={(event) => setVideoCalls(event.target.value)}
              min="0"
              max="25"
              step="1"
            />
          </div>
        </div>

        <div className="band-item">
          <h5>Cloud Based Security Cams/Doorbells</h5>
          <img
            src={require(`../../images/icon-security.png`)}
            alt="Security Cams / Doorbells"
          />
          <div className="cams-types">
            <div
              className={`cams-type ${camsType === "HD" ? "active" : ""}`}
              onClick={() => setCamsType("HD")}
            >
              <p>HD</p>
            </div>
            <div
              className={`cams-type ${camsType === "4K" ? "active" : ""}`}
              onClick={() => setCamsType("4K")}
            >
              <p>4K</p>
            </div>
          </div>
          <div className="band-count">
            <div className="band-count-input">
              <i className="icon icon-users" />
              <input
                type="number"
                value={cams}
                onChange={(event) => setCams(event.target.value)}
                min="0"
              />
            </div>
            <input
              type="range"
              value={cams}
              onChange={(event) => setCams(event.target.value)}
              min="0"
              max="25"
              step="1"
            />
          </div>
        </div>

        <div className="band-item">
          <h5>VoIP</h5>
          <img src={require(`../../images/icon-headset.png`)} alt="VoIP" />
          <div className="band-count">
            <div className="band-count-input">
              <i className="icon icon-users" />
              <input
                type="number"
                value={voip}
                onChange={(event) => setVoip(event.target.value)}
                min="0"
              />
            </div>
            <input
              type="range"
              value={voip}
              onChange={(event) => setVoip(event.target.value)}
              min="0"
              max="25"
              step="1"
            />
          </div>
        </div>

        <div className="band-item">
          <h5>Media / File Sharing</h5>
          <img
            src={require(`../../images/icon-media.png`)}
            alt="Media / File Sharing"
          />
          <div className="band-count">
            <div className="band-count-input">
              <i className="icon icon-users" />
              <input
                type="number"
                value={media}
                onChange={(event) => setMedia(event.target.value)}
                min="0"
              />
            </div>
            <input
              type="range"
              value={media}
              onChange={(event) => setMedia(event.target.value)}
              min="0"
              max="25"
              step="1"
            />
          </div>
        </div>

        <div className="band-item">
          <h5>
            Upload/Download Large Files<span>(Files &gt;50MB)</span>
          </h5>
          <img
            src={require(`../../images/icon-download.png`)}
            alt="Up/Downloading Files"
          />
          <div className="band-count">
            <div className="band-count-input">
              <i className="icon icon-users" />
              <input
                type="number"
                value={files}
                onChange={(event) => setFiles(event.target.value)}
                min="0"
              />
            </div>
            <input
              type="range"
              value={files}
              onChange={(event) => setFiles(event.target.value)}
              min="0"
              max="25"
              step="1"
            />
          </div>
        </div>

        <div className="band-total">
          <Progress
            value={totalBand}
            total="1850"
            progress="value"
            precision={0}
          />
          <h3>
            We recommend the: <span>{getPlanType()} plan</span>
          </h3>
          <button
            className="btn btn-quaternary"
            onClick={() => setModalOpen(true)}
          >
            Get started today
          </button>
        </div>

        <Modal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEmailSent(false);
          }}
          className="modal-contact-form"
        >
          <span
            className="modal-close"
            onClick={() => {
              setModalOpen(false);
              setEmailSent(false);
            }}
          >
            <i className="icon icon-cross" />
          </span>
          <Modal.Header>
            <h3>Get started with Business Connect</h3>
          </Modal.Header>
          <Modal.Content>
            <form className="contact-form" onSubmit={onSubmit}>
              <input
                type="text"
                value={name}
                placeholder="* Contact Name"
                onChange={(event) => setName(event.target.value)}
              />
              <input
                type="email"
                value={contactEmail}
                placeholder="* Email"
                onChange={(event) => setContactEmail(event.target.value)}
              />
              <input
                type="tel"
                value={phone}
                placeholder="Phone"
                onChange={(event) => setPhone(event.target.value)}
              />
              <input
                type="text"
                value={business}
                placeholder="Business Name"
                onChange={(event) => setBusiness(event.target.value)}
              />
              <Autocomplete
                ref={locationRef}
                apiKey={"AIzaSyDredNL2X4rpna--oT1lEdOvxLbyYJgmyo"}
                options={{
                  types: ["address"],
                  componentRestrictions: { country: "us" },
                }}
                placeholder="* Address"
                id="location-autocomplete"
                onPlaceSelected={(place) => setContactLocation(place)}
              />
              <Checkbox
                label="Do you require a service level agreement?"
                checked={agreement}
                onChange={(e, data) => setAgreement(data.checked)}
              />
              <textarea
                value={contactMessage}
                placeholder="Anything else you'd like us to know?"
                rows="4"
                onChange={(event) => setContactMessage(event.target.value)}
              />
              <button
                id="submit"
                className={`btn btn-quaternary ${
                  emailSent ? "btn-success" : ""
                } ${emailSending ? "sending" : ""}`}
                type="submit"
                disabled={isInvalid}
              >
                {!emailSending && !emailSent ? (
                  <span>send message</span>
                ) : !emailSending && emailSent ? (
                  <span>
                    <i className="icon icon-checkmark-circle" /> message sent!
                  </span>
                ) : (
                  <span className="btn-loading" />
                )}
              </button>
              {error.current && (
                <p className="error">{error.current.message}</p>
              )}
            </form>
          </Modal.Content>
        </Modal>
      </div>
    </section>
  );
};

export default BandwidthCalcPage;
